import '@/input.css'
import '@/styles/utils/vue-toastification.scss'

import * as Sentry from '@sentry/vue'
import { VueQueryPlugin } from '@tanstack/vue-query'
import { createApp } from 'vue'
import Toast from 'vue-toastification'

import { setGlobalI18nInstance, setupI18n } from './lib/i18n'
import App from './App.vue'
import router from './router'

import fetchClient from '@/lib/fetch'
import { initPosthog, PosthogClient } from '@/lib/posthog'
import queryClient from '@/lib/vue-query'
import { ANALYTICS_DISABLED_KEY } from '@/utils/config/constants'

// internationalization
let i18nInstance
export { i18nInstance }

// Sentry beforeSend: filter noise + rate-limit re-render loops.
// Defined at module level so the Cypress test hook can access it synchronously.
function sentryBeforeSend(event) {
  const exception = event.exception?.values?.[0]

  // Ignore Safari/WebKit "Load failed" — raised when fetch() is aborted on navigation (KC-463)
  const isWebKitAbort =
    exception?.type === 'TypeError' &&
    (exception?.value ?? '').includes('Load failed') &&
    !exception?.stacktrace?.frames?.length &&
    exception?.mechanism?.type === 'onunhandledrejection'
  if (isWebKitAbort) return null

  // Bruit connu, jamais notre code (routine sentry-bugs, motifs vus a chaque run du 10 au 17/09/2026) :
  // - reseau client (offline, coupure) : « Load failed » / « Failed to fetch » meme avec des frames, quand
  //   celles-ci ne viennent que du Session Replay PostHog (posthog-recorder / rrweb) qui enveloppe fetch
  // - traduction automatique du navigateur (Chrome/Edge/app Google iOS) qui reecrit le DOM sous Vue en <font>
  // - API d extension appelee dans la page (runtime.sendMessage), pont natif de la webview Snapchat
  // - Session Replay (rrweb) qui lit une iframe tierce (Whereby)
  // Un evenement ecarte est compte (window.__SENTRY_NOISE_DROPPED__ + event PostHog `sentry:noise_dropped`)
  // pour que le zero cote Sentry reste mesurable : un motif trop large se verrait dans PostHog.
  const noise = isKnownNoise(exception)
  if (noise) {
    countDroppedNoise(noise, exception)
    return null
  }

  // Rate-limit identical events per session to avoid flooding Sentry
  // when a reactive computation throws in a re-render loop (KC-466)
  const MAX_EVENTS_PER_FINGERPRINT = 5
  const lastFrame = exception?.stacktrace?.frames?.slice(-1)[0]
  const fingerprint = [
    exception?.type ?? '',
    exception?.value ?? event.message ?? '',
    lastFrame?.filename ?? '',
    lastFrame?.lineno ?? '',
  ].join('|')
  const counts = (window.__SENTRY_EVENT_COUNTS__ = window.__SENTRY_EVENT_COUNTS__ || {})
  counts[fingerprint] = (counts[fingerprint] || 0) + 1
  if (counts[fingerprint] > MAX_EVENTS_PER_FINGERPRINT) {
    return null
  }

  return scrubSentryPii(event)
}

// RGPD (2026-10-22) : aucune donnee personnelle ne part chez Sentry. On ne garde que l id technique
// de l utilisateur et on masque emails et jetons dans les messages, exceptions, URLs et breadcrumbs.
const PII_EMAIL = /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi
const PII_PARAM = /([?&](?:token|access_token|refresh_token|code|password|email|key|signature)=)[^&#]*/gi
function scrubPiiString(value) {
  return typeof value === 'string' ? value.replace(PII_EMAIL, '[email]').replace(PII_PARAM, '$1[filtre]') : value
}
function scrubSentryPii(event) {
  if (event.user) event.user = event.user.id !== undefined ? { id: event.user.id } : undefined
  if (event.request) {
    delete event.request.cookies
    delete event.request.data
    event.request.url = scrubPiiString(event.request.url)
    event.request.query_string = scrubPiiString(event.request.query_string)
  }
  event.message = scrubPiiString(event.message)
  event.exception?.values?.forEach((exception) => {
    exception.value = scrubPiiString(exception.value)
  })
  event.breadcrumbs?.forEach((breadcrumb) => {
    breadcrumb.message = scrubPiiString(breadcrumb.message)
    if (breadcrumb.data?.url) breadcrumb.data.url = scrubPiiString(String(breadcrumb.data.url))
  })
  return event
}

// Chaque motif porte un libelle stable : c est la cle du compteur et la propriete `motif` de l event PostHog.
const NOISE_VALUE_PATTERNS = [
  [/Failed to execute '(insertBefore|removeChild|appendChild)' on 'Node'/, 'browser-translate'],
  [/The object can not be found here/, 'browser-translate'],
  [/Invalid call to runtime\.sendMessage/, 'browser-extension'],
  [/No Listener: tabs:/, 'browser-extension'],
  [/SCDynimacBridge|SCDynamicBridge/, 'snapchat-webview'],
  [/Blocked a frame with origin/, 'replay-cross-origin-iframe'],
]
// Le SDK Sentry suffixe l hote a l erreur reseau : « Failed to fetch (api.ieseg.constel-education.com) »
const NETWORK_VALUE_PATTERNS = [
  /^Load failed/,
  /^Failed to fetch( \([^)]*\))?$/,
  /^NetworkError when attempting to fetch resource/,
]
const REPLAY_FRAME_PATTERN = /posthog-recorder|rrweb|extensions\/replay/
const isReplayFrame = (f) => REPLAY_FRAME_PATTERN.test(f?.filename ?? '')
// frame sans fichier : generateur async (`<anonymous>` next) ou code natif
const isAnonymousFrame = (f) => !f?.filename || f.filename === '<anonymous>' || f.filename === '[native code]'

/**
 * Vrai quand la pile d une erreur reseau ne contient que la chaine d enveloppes de fetch, sans code applicatif.
 * Le recorder du Session Replay (posthog-recorder / rrweb) enveloppe window.fetch ; sous Chrome / Edge la pile
 * async porte en plus le generateur (`<anonymous>` next) et, en position la plus interne, l instrumentation
 * fetch de Sentry, qui vit dans notre bundle mais n est pas notre code (WEB_APP-154, variante Chrome, 24/09/2026).
 * Le code applicatif, quand la pile le porte, est l APPELANT de fetch : il se trouve avant (au-dessus de) la
 * premiere frame du recorder. Une frame applicative a cet endroit fait partir l evenement.
 */
function isReplayOnlyNetworkStack(frames) {
  if (frames.length === 0) return true
  const first = frames.findIndex(isReplayFrame)
  if (first === -1) return false
  const outer = frames.slice(0, first)
  const inner = frames.slice(first)
  const foreign = inner.filter((f) => !isReplayFrame(f) && !isAnonymousFrame(f))
  const onlyInnermostIsForeign =
    foreign.length === 0 || (foreign.length === 1 && foreign[0] === inner[inner.length - 1])
  return outer.every(isAnonymousFrame) && onlyInnermostIsForeign
}
// Script injecte par un logiciel de securite (Trend Micro Toolbar, namespace `tmsa`) qui reecrit setTimeout et
// EventTarget.addEventListener : la frame fautive n a pas de fichier (`<anonymous>`), seule sa fonction la nomme.
// WEB_APP-15A / WEB_APP-159 (IESEG, 21/09/2026).
const INJECTED_SCRIPT_FRAME_PATTERN = /^tmsa\b|tmsaLogBehavior|^EventTarget\.addEventListener$/

/** Rend le libelle du motif de bruit reconnu, ou null si l evenement doit partir. */
function isKnownNoise(exception) {
  const value = exception?.value ?? ''
  const frames = exception?.stacktrace?.frames ?? []
  const byValue = NOISE_VALUE_PATTERNS.find(([re]) => re.test(value))
  if (byValue) return byValue[1]
  if (NETWORK_VALUE_PATTERNS.some((re) => re.test(value))) {
    // reseau client : la pile ne porte que les enveloppes de fetch du Session Replay (ou il n y en a pas)
    return isReplayOnlyNetworkStack(frames) ? 'client-network-replay' : null
  }
  if (
    frames.some((f) => (f?.filename ?? '') === '<anonymous>' && INJECTED_SCRIPT_FRAME_PATTERN.test(f?.function ?? ''))
  ) {
    return 'injected-security-script'
  }
  return null
}

function countDroppedNoise(motif, exception) {
  const counts = (window.__SENTRY_NOISE_DROPPED__ = window.__SENTRY_NOISE_DROPPED__ || {})
  counts[motif] = (counts[motif] || 0) + 1
  try {
    // PostHog peut etre desactive pour l utilisateur (ANALYTICS_DISABLED_KEY) ou pas encore initialise
    if (PosthogClient.__loaded) {
      PosthogClient.capture('sentry:noise_dropped', {
        motif,
        exception_type: exception?.type ?? '',
        exception_value: (exception?.value ?? '').slice(0, 120),
      })
    }
  } catch {
    // le compteur fenetre suffit ; ne jamais faire echouer beforeSend
  }
}

// Test hook (Cypress): expose sentryBeforeSend for unit-testing the filter logic
if (window.__CYPRESS_EXPOSE_BEFORE_SEND__) {
  window.__sentry_before_send__ = sentryBeforeSend
}

async function init() {
  i18nInstance = await setupI18n()
  setGlobalI18nInstance(i18nInstance)

  // Test hook (Cypress): force an unloaded locale to reproduce i18n race at boot
  if (window.__CYPRESS_FORCE_UNLOADED_LOCALE__) {
    i18nInstance.global.locale.value = window.__CYPRESS_FORCE_UNLOADED_LOCALE__
  }

  // Test hook (Cypress): expose fetchClient for direct testing of fetch wrapper
  if (window.__CYPRESS_EXPOSE_FETCH_CLIENT__) {
    window.__fetchClient__ = fetchClient
  }

  const app = createApp(App)

  app.use(VueQueryPlugin, {
    queryClient,
  })

  app.use(router)
  app.use(Toast, {})
  app.use(i18nInstance)

  // disable all analytics tools for the user
  if (window.localStorage.getItem(ANALYTICS_DISABLED_KEY)) {
    console.warn('***********************')
    console.warn('ANALYTICS ARE DISABLED FOR THIS USER')
    console.warn('***********************')
  }

  // posthog script
  if (!window.localStorage.getItem(ANALYTICS_DISABLED_KEY)) {
    initPosthog()
  }

  // brevo script
  if (import.meta.env.VITE_ENV === 'production' && !window.localStorage.getItem(ANALYTICS_DISABLED_KEY)) {
    const brevo = document.createElement('script')
    brevo.id = 'brevo-conversations'
    brevo.type = 'text/javascript'
    brevo.innerHTML = `
        !function(e,n,o){n.BrevoConversationsID="611ce099bcb2d0387209fc56",n[o]=n[o]||function(){(n[o].q=n[o].q||[]).push(arguments)};var s=e.createElement("script");s.async=!0,s.src="https://conversations-widget.brevo.com/brevo-conversations.js",e.head&&e.head.appendChild(s)}(document,window,"BrevoConversations");
        `

    // Brevo script will be injected when white label data is loaded
    window.brevoScript = brevo
  }

  // sentry script
  if (import.meta.env.VITE_ENV === 'production' && !window.localStorage.getItem(ANALYTICS_DISABLED_KEY)) {
    Sentry.init({
      app,
      // DSN surchargeable au build (VITE_SENTRY_DSN) : prepare la bascule vers GlitchTip auto-heberge.
      dsn:
        import.meta.env.VITE_SENTRY_DSN ||
        'https://b981e456aa676c7d459c801ca47f1477@o995149.ingest.sentry.io/4506739714752512',
      // RGPD (2026-10-22) : erreurs seules. Plus de Session Replay (il enregistrait le texte des pages
      // en clair, conversations /chat comprises : maskAllText: false) ni de traces de performance.
      sendDefaultPii: false,
      beforeSend: sentryBeforeSend,
    })
  }

  app.mount('#app')
}

init()
